/* MATTER: what every kind of thing IS, so that what happens between things
 * can be written once.
 *
 * The game used to spell each interaction by hand -- the ember lights a
 * brazier, a flame walks onto thorn, water stops a flame -- one branch per
 * pair, and a pair nobody wrote never happened. Here a tile kind and a
 * creature kind carry PROPERTIES (how flammable, whether wet, brittle, heavy,
 * lit, sheltering; what a creature fears and what draws it), and the
 * REACTIONS below read only those. Fire reads flammability, dryness and the
 * wind; a blast reads brittleness; water reads what floats; a creature reads
 * fear and draw. Add a kind with its properties and every reaction already
 * knows it. That is the whole bet: the more the table says, the more the
 * world does that nobody scripted.
 *
 * Everything here is pure and seeded. The world stays a function of its seed
 * and its clock, which is what every suite relies on. */
import { T } from './art.ts';
import { mix } from './rng.ts';

/* ---------------------------------------------------------------- tiles --- */

export type Matter = {
  flammable: number;   // 0..1: chance per tick of catching from a burning neighbour, before dryness and wind
  wet: boolean;        // puts fire out, carries what floats, and is the tide's
  brittle: boolean;    // a blast breaks it
  heavy: boolean;      // stops a launch; a boulder, a wall, a face
  chops: number;       // blows of the axe to fell it (LS-384): a tree comes down in three and leaves logs
  lit: boolean;        // gives light at night
  shelter: boolean;    // a creature hides in it from rain and fire
  fuel: number;        // ticks it burns before it is spent
  ash: number | null;  // what it becomes when burnt out (null: unchanged)
  regrows: number | null;   // what scorched ground grows back into, given time and water near
  dry: boolean;        // cut and dried: burns at full dryness whatever the biome or the hour
};

const M = (o: Partial<Matter>): Matter => ({ flammable: 0, wet: false, brittle: false, heavy: false, chops: 0,
  lit: false, shelter: false, fuel: 0, ash: null, regrows: null, dry: false, ...o });

export const MATTER: Record<number, Matter> = {
  [T.THORN]:   M({ flammable: 1.0, fuel: 26, ash: T.SCORCH, brittle: false, shelter: true }),
  [T.TREE]:    M({ flammable: 0.35, fuel: 90, ash: T.SCORCH, heavy: true, shelter: true, chops: 3 }),
  [T.MOSS]:    M({ flammable: 0.12, fuel: 30, ash: T.SCORCH }),
  [T.GROUND]:  M({ flammable: 0.05, fuel: 20, ash: T.SCORCH }),
  [T.SAND]:    M({}),
  [T.DEBRIS]:  M({ flammable: 0.08, fuel: 20, ash: T.SCORCH }),
  [T.SCORCH]:  M({ regrows: T.GROUND }),
  [T.BONES]:   M({ regrows: T.GROUND }),
  [T.ROD]:     M({ heavy: true }),   // the lightning rod (LS-381): it stands, and the storm goes to it
  [T.ICE]:     M({}),   // ice (LS-381): not wet, not fuel; the tide writes it back to water when the ground warms
  [T.FORD]:    M({}),   // a ford (LS-383): dry sand between tides; the tide's own record makes it water   // the pack's kill mark (LS-380): the ground takes the bones back as it takes the scorch
  /* CUT GRASS IS A FUSE (LS-176 step 4). The card sketched 0.7 and 8; the
   * numbers here are what a fuse needs: a burning tile catches each
   * neighbour with probability flammable * dryness * 0.35 per fire tick,
   * so at 0.9 and full dryness a tuft passes the fire on with 0.315 a tick
   * and in twelve ticks of fuel fails to one time in a hundred. A five-tuft
   * fuse laid from a fire to a hedge runs almost every time; the same five
   * tiles of moss (0.12, and damp in a fen) hardly ever carry it. */
  [T.TUFT]:    M({ flammable: 0.9, fuel: 12, ash: T.SCORCH, dry: true, regrows: T.MOSS }),
  [T.WATER]:   M({ wet: true }),
  [T.SHALLOW]: M({ wet: true }),
  [T.SEA]:     M({ wet: true }),
  [T.ROCK]:    M({ brittle: true, heavy: true }),
  [T.RUBBLE]:  M({ brittle: true }),
  [T.CRACK]:   M({ brittle: true }),
  [T.CLIFF]:   M({ heavy: true }),
  [T.BRAZIER]: M({ flammable: 1.0, fuel: 0, brittle: false }),   // it does not burn out, it lights
  [T.BRAZIER_LIT]: M({ lit: true }),
  [T.NEST]:    M({ flammable: 0.6, fuel: 40, ash: T.SCORCH, brittle: true, shelter: true }),
  [T.STONE]:   M({ heavy: true }),
  [T.CAIRN]:   M({ brittle: true, heavy: true }),
};

export const matterOf = (kind: number): Matter => MATTER[kind] || M({});

/* ------------------------------------------------------------ creatures --- */

export type Traits = {
  fearsFire: number;     // 0..1: how far it will go to get away from a flame
  drawnToLight: number;  // 0..1: at night, how strongly a lit brazier pulls it
  burns: boolean;        // fire hurts it (a wisp is made of light and does not)
  floats: boolean;       // deep water carries it
  nests: boolean;        // spawned from a nest, returns to one to spend itself
  carriesGlim: boolean;  // drops glim when killed
  eatsFire?: number;     // 0..1: how strongly a fire pulls it, and it eats what it reaches (the cinder, LS-293)
};

const TR = (o: Partial<Traits>): Traits => ({ fearsFire: 0.5, drawnToLight: 0, burns: true,
  floats: false, nests: false, carriesGlim: true, ...o });

export const TRAITS: Record<string, Traits> = {
  sporeling: TR({ fearsFire: 0.8, floats: true, nests: true }),
  bramble:   TR({ fearsFire: 1.0 }),               // a bramble is kindling on legs
  wisp:      TR({ fearsFire: 0, drawnToLight: 1.0, burns: false, floats: true, carriesGlim: false }),
  gloam:     TR({ fearsFire: 0, drawnToLight: 1.0, burns: true, floats: true, carriesGlim: false }),   // the night moth (LS-293): goes to the light, and the light is what kills it
  cinder:    TR({ fearsFire: 0, burns: false, eatsFire: 1.0, carriesGlim: false }),   // the fire-eater (LS-293): fire is what it wants, and each one eaten is a life more
  eel:       TR({ fearsFire: 0, burns: false, floats: true, carriesGlim: false }),   // the silt eel (LS-293): deep water is where it lives
  shellback: TR({ fearsFire: 0.3, floats: false }),
  tidecrab:  TR({ fearsFire: 0.4, floats: false, burns: false }),   // shelled, and the sea is its own
  mossback:  TR({ fearsFire: 1.0, floats: true, burns: true }),      // soft and wet: fire is death and it knows it
  sunbasker: TR({ fearsFire: 0.5, floats: false, burns: true }),
  screehopper: TR({ fearsFire: 0, floats: false, burns: false }),   // shelled and fearless: a torch does not keep it off
  stormgull: TR({ fearsFire: 0.2, floats: true, burns: false }),     // wet feathers do not burn; a torch barely turns it
  duskling:  TR({ fearsFire: 1.0, floats: false, burns: true }),      // it hides from a flame in the nearest thicket
  charger:   TR({ fearsFire: 0.6 }),
};

export const traitsOf = (kind: string): Traits => TRAITS[kind] || TR({});

/* ---------------------------------------------------------------- clock --- */

/* One world clock, in game-minutes, from the run's frame count. Everything
 * that changes over time reads it: the tide, the day, regrowth, nests.
 * Seeded per world so two runs of one seed agree, and a screen returned to
 * after an hour away catches up by the same arithmetic. */
export const FRAMES_PER_MINUTE = 60 * 60;
export const TIDE_PERIOD = 6;      // game-minutes from high water to high water
export const DAY_PERIOD = 24;      // game-minutes in a day

/* A WORLD'S MOOD (2026-09-27, docs/DIRECTION.md: seeds differ by which system rules them). Every seed is one of three:
 * DRY -- rain is rare, the air a touch warmer and fire catches faster, so the fire is everything; TIDAL -- the tide runs
 * nearly twice as far and nearly twice as fast, and its ebb bares the shallows and the fords and turns a deep lake
 * wadeable, so the ebb is the road; COLD -- the air is colder and it rains more, so standing water freezes to ice you
 * can walk. The same verbs, a different game. `__mood` pins one for a fixture ('none' is the plain world the old checks
 * were written against). */
export type Mood = 'dry' | 'tidal' | 'cold' | 'none';
export const MOODS: Mood[] = ['dry', 'tidal', 'cold'];
export const MOOD: Record<Mood, { rain: number; warm: number; dry: number; tide: number; period: number; bare: boolean }> = {
  none: { rain: 0.62, warm: 0, dry: 1, tide: 1, period: 1, bare: false },
  dry: { rain: 0.86, warm: 0.02, dry: 1.35, tide: 1, period: 1, bare: false },   // warm 0.02, not 0.08: at 0.08 the dust scorched all day and the newcomer probe lost eleven vigor to the ground; the dry world is the fire's, not the heat's
  tidal: { rain: 0.62, warm: 0, dry: 1, tide: 1.8, period: 0.6, bare: true },
  cold: { rain: 0.54, warm: -0.2, dry: 0.9, tide: 1, period: 1, bare: false },
};
export function moodOf(seedNum: number): Mood {
  const pin = (globalThis as any).__mood; if (pin && MOOD[pin as Mood]) return pin;
  return MOODS[((mix(seedNum, 5501, 3) >>> 6) & 1023) % 3];
}
export const moodOfSeed = (seedNum: number) => MOOD[moodOf(seedNum)];

/** THE WEATHER (LS-223): how hard it rains at a minute of the run, 0 to 1, from a slow seeded noise over the
 *  clock: a shower every few hours of play, a downpour now and then, the same on the same world twice. */
export function rainAt(seedNum: number, minutes: number): number {
  /* A RUN BEGINS IN FAIR WEATHER -- FOR THREE MINUTES, NOT THIRTY (2026-09-18). A game-minute is 3,600 frames,
   * which is a real minute at sixty frames a second, and a run is about twenty of them (the quest bot wins in
   * three to five). So a thirty-minute grace put the FIRST rain of every world past the end of every run: measured
   * over two hundred seeds, no world saw rain, a soaking, a storm or a strike within twenty minutes of play, and
   * the median first rain was at 32.7 minutes. Everything the weather feeds -- Soaked, the storm, the lightning,
   * the stormgull, the oilskin, the bitterleaf, the ice the cold makes of standing water -- was content no player
   * could reach. Three minutes still opens a run in the dry; a check that needs a dry world uses
   * `systems.rainOverride`, which is what it is for. */
  if (minutes < 3) return 0;
  const cell = 9;   // game-minutes a spell of weather lasts
  const f = minutes / cell, i = Math.floor(f), t = f - i, sm = t * t * (3 - 2 * t);
  const at = (n) => ((mix(seedNum + 7331, n, 5) >>> 8) & 1023) / 1024;
  const v = at(i) * (1 - sm) + at(i + 1) * sm;   // 0..1, smooth
  const line = moodOfSeed(seedNum).rain;   // the mood moves the line the noise must clear: a dry world rarely, a cold one more often
  return Math.max(0, Math.min(1, (v - line) / (1 - line)));   // dry most of the time; rain when the noise runs high
}
export function clockOf(seedNum: number, frame: number) {
  const minutes = frame / FRAMES_PER_MINUTE;
  const phase = ((mix(seedNum, 7, 7) & 1023) / 1024);      // where in the cycle the world starts
  const tide = Math.sin(((minutes / (TIDE_PERIOD * moodOfSeed(seedNum).period)) + phase) * Math.PI * 2);   // -1 ebb .. +1 flood; a tidal world's runs faster
  const day = ((minutes / DAY_PERIOD) + phase * 0.5) % 1;                    // 0 dawn .. 0.5 dusk .. 1 dawn
  return { minutes, tide, day, night: day > 0.55 && day < 0.95 };
}

/* ------------------------------------------------------------ reactions --- */

export type Grid = {
  cols: number; rows: number;
  at(x: number, y: number): number | undefined;     // tile kind, undefined off the grid
  set(x: number, y: number, v: number): void;
};

/** Dryness of a tile: 1 in a dry biome at midday, near 0 beside water. */
export function drynessAt(grid: Grid, x: number, y: number, biomeDry: number, day: number) {
  const here = grid.at(x, y);
  if (here !== undefined && matterOf(here).dry) return 1;   // cut straw is dry in any weather
  let wetNear = 0;
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const v = grid.at(x + dx, y + dy);
    if (v !== undefined && matterOf(v).wet) wetNear++;
  }
  const noon = 1 - Math.abs(day - 0.3) * 1.4;                 // driest a little after noon
  return Math.max(0, Math.min(1, biomeDry * (0.6 + 0.4 * Math.max(0, noon)) - wetNear * 0.2));
}

export const BIOME_DRY: Record<string, number> = { dust: 1.0, scree: 0.8, ash: 0.9, heath: 0.6, bloom: 0.5, fen: 0.2 };

export type Fire = { x: number; y: number; fuel: number; born: number; kind?: number };   // kind: what was burning

/** One tick of a fire over a grid. Returns the tiles that caught this tick.
 *  A burning tile spends fuel; when spent it becomes its ash. Each burning
 *  tile tries its four neighbours: a neighbour catches with probability
 *  flammable * dryness * wind-toward, and never if it is wet, locked, or
 *  already burning. Seeded by (seed, tick, x, y): the same fire on the same
 *  world burns the same way twice. */
export function tickFire(grid: Grid, fires: Fire[], seedNum: number, tick: number, biomeDry: number,
  day: number, wind: [number, number]): { caught: Fire[]; spent: Fire[] } {
  const burning = new Set(fires.map((f) => f.x + ',' + f.y));
  const caught: Fire[] = [], spent: Fire[] = [];
  for (const f of fires) {
    f.fuel--;
    if (f.fuel <= 0) {
      const m = matterOf(grid.at(f.x, f.y) ?? -1);
      if (m.ash !== null) grid.set(f.x, f.y, m.ash);
      spent.push(f);
      continue;
    }
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = f.x + dx, ny = f.y + dy;
      const v = grid.at(nx, ny);
      if (v === undefined || burning.has(nx + ',' + ny)) continue;
      const m = matterOf(v);
      if (!m.flammable || m.wet) continue;
      const toward = 1 + 0.6 * (dx * wind[0] + dy * wind[1]);   // downwind catches sooner, upwind later
      const p = m.flammable * drynessAt(grid, nx, ny, biomeDry, day) * toward * 0.35;
      const roll = ((mix(seedNum, tick * 31 + nx, ny * 17 + 3) >>> 8) & 1023) / 1024;
      if (roll < p) { burning.add(nx + ',' + ny); caught.push({ x: nx, y: ny, fuel: m.fuel || 1, born: tick, kind: v }); }
    }
  }
  return { caught, spent };
}

/** Scorched ground grows back: after `after` minutes it is ground again, and
 *  beside water it comes back as thorn -- a hedge is what a burnt shore grows. */
export function regrow(grid: Grid, x: number, y: number, minutesScorched: number, after = 8): number | null {
  const v = grid.at(x, y);
  if (v !== T.SCORCH || minutesScorched < after) return null;
  let wetNear = false;
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    const n = grid.at(x + dx, y + dy);
    if (n !== undefined && matterOf(n).wet) wetNear = true;
  }
  const next = wetNear ? T.THORN : T.GROUND;
  grid.set(x, y, next);
  return next;
}

/** Where the tide stands on a wet tile: deep water at the flood is deep,
 *  shallow at the ebb; a shallow at the flood is deep. Never changes what a
 *  bare hand can cross -- both kinds are the tide's -- so the gating proof
 *  holds at every hour. */
export function tideKind(base: number, tide: number): number {
  if (base === T.WATER) return tide < -0.6 ? T.SHALLOW : T.WATER;
  if (base === T.SHALLOW) return tide > 0.6 ? T.WATER : T.SHALLOW;
  return base;
}

/** What a creature wants to do about its surroundings: a vector away from
 *  fire it fears and toward light that draws it, in tiles. Zero when nothing
 *  moves it; the caller adds it to whatever it was doing. */
export function urge(kind: string, x: number, y: number, fires: Fire[], lights: { x: number; y: number }[], night: boolean): [number, number] {
  const tr = traitsOf(kind);
  let ux = 0, uy = 0;
  if (tr.fearsFire) {
    for (const f of fires) {
      const dx = x - f.x, dy = y - f.y, d = Math.hypot(dx, dy);
      if (d < 4 && d > 0) { ux += (dx / d) * tr.fearsFire * (4 - d); uy += (dy / d) * tr.fearsFire * (4 - d); }
    }
  }
  if (tr.eatsFire) {   // the cinder is pulled to a fire the way a wisp is to a light, by day or night, and closer
    for (const f of fires) {
      const dx = f.x - x, dy = f.y - y, d = Math.hypot(dx, dy);
      if (d < 9 && d > 0.3) { ux += (dx / d) * tr.eatsFire; uy += (dy / d) * tr.eatsFire; }
    }
  }
  if (tr.drawnToLight && night) {
    for (const l of lights) {
      const dx = l.x - x, dy = l.y - y, d = Math.hypot(dx, dy);
      if (d < 9 && d > 1.5) { ux += (dx / d) * tr.drawnToLight; uy += (dy / d) * tr.drawnToLight; }
    }
  }
  return [ux, uy];
}
